import { useCallback, useMemo } from 'react';
import { Image3D } from '~/domain/patient/image3d';
import type { ContourModelChoice } from '~/state/atoms/adaptiveReplanningAtoms';
import { useAutoSegmentation } from './useAutoSegmentation';
import { useComparisonContourModel } from './useComparisonContourModel';
import { useQaModelRois } from './useQaModelRois';

export interface ComparisonContouring {
    readonly start: (image: Image3D) => Promise<string | undefined>;
}

export function useComparisonContouring(
    choice: ContourModelChoice | undefined
): ComparisonContouring {
    const { startProcessing } = useAutoSegmentation();
    const model = useComparisonContourModel(choice);
    const customizationName = choice?.customizationName;
    // Every structure the customization offers, the same list the ROI plan is built from.
    const modelRois = useQaModelRois(choice);
    const roiNames = useMemo(
        () => modelRois.map((roi) => roi.name),
        [modelRois]
    );

    const start = useCallback(
        async (image: Image3D) => {
            // An empty list would ask for the model's default subset, not all of it.
            if (!model || roiNames.length === 0) {
                return undefined;
            }
            return startProcessing(model, roiNames, customizationName, {
                image,
                isBackgroundComparison: true,
            });
        },
        [model, roiNames, customizationName, startProcessing]
    );

    return { start };
}
